import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, CalendarDays, Plus, Globe, LogOut, KeyRound, ChevronDown, PenLine, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useSalesAuth } from './SalesAuthContext';
import { useT } from './salesI18n';
import { salesApi } from './salesApi';
import { LeadFormDialog } from './components/LeadFormDialog';
import { ChangePasswordDialog } from './components/ChangePasswordDialog';
import { SignatureDialog } from './components/SignatureDialog';
import { RepPhotoDialog } from './components/RepPhotoDialog';
import { SalesNotificationsProvider } from './hooks/useSalesNotifications';
import { NotificationBell } from './components/NotificationBell';

const LOGO = `${process.env.PUBLIC_URL || ''}/sales-logo.png`;
const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';

export default function SalesLayout() {
  const { user, logout } = useSalesAuth();
  const { t, lang, setLang } = useT();
  const navigate = useNavigate();
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const photoRequired = Boolean(user) && user.has_photo === false; // sem foto ainda: aviso obrigatório ao entrar
  const photoUrl = user?.photo_token ? `${process.env.REACT_APP_BACKEND_URL}/api/sales/rep-photo/${user.photo_token}.jpg` : null;

  const nav = [
    { to: '/', label: t.dashboard, icon: LayoutDashboard, end: true, id: 'dashboard' },
    { to: '/leads', label: t.leads, icon: Users, id: 'leads' },
    { to: '/agenda', label: t.agenda, icon: CalendarDays, id: 'agenda' },
  ];

  const toggleLang = async () => {
    const next = lang === 'en' ? 'pt-BR' : 'en';
    setLang(next);
    try { await salesApi.put('/auth/language', { language: next }); } catch (e) { /* silencioso */ }
  };

  const pillCls = ({ isActive }) => `inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm transition-all duration-200 ${isActive ? 'bg-teal-600 text-white font-semibold shadow-[0_6px_14px_-6px_rgba(13,148,136,0.6)]' : 'text-slate-600 font-medium hover:text-slate-900 hover:bg-white/60'}`;

  return (
    <SalesNotificationsProvider>
    <div className="min-h-[100dvh] flex flex-col">
      <header className="sticky top-0 z-30 px-3 sm:px-6 pt-3 sm:pt-4">
        <div className="max-w-[1400px] mx-auto sales-glass-strong rounded-full h-14 sm:h-16 px-3 sm:px-5 flex items-center gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src={LOGO} alt="PM Vendas" className="h-12 sm:h-14 w-auto object-contain drop-shadow-sm" data-testid="sales-header-logo" />
            <div className="min-w-0 hidden sm:block">
              <p className="font-bold leading-none text-base text-slate-900 truncate">{t.appName}</p>
              <p className="text-[10px] text-teal-700 font-semibold tracking-[0.18em] uppercase mt-1 truncate">{t.tagline}</p>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-1 mx-auto bg-white/60 border border-white/80 rounded-full p-1" data-testid="sales-top-nav">
            {nav.map((n) => (
              <NavLink key={n.id} to={n.to} end={n.end} className={pillCls} data-testid={`sales-nav-${n.id}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 ml-auto">
            <button type="button" onClick={() => setNewLeadOpen(true)} data-testid="sales-nav-new-lead" className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold shadow-[0_6px_16px_-6px_rgba(13,148,136,0.6)] transition-all duration-200 hover:-translate-y-px active:scale-95">
              <Plus className="h-4 w-4" /> {t.newLead}
            </button>
            <NotificationBell />
            <button type="button" onClick={toggleLang} data-testid="sales-lang-toggle" className="inline-flex items-center gap-1.5 h-10 px-3 rounded-full bg-white/70 border border-white/80 text-xs font-semibold text-slate-700 hover:bg-white transition-colors">
              <Globe className="h-3.5 w-3.5" /> {lang === 'en' ? 'EN' : 'PT'}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" data-testid="sales-user-menu" className="inline-flex items-center gap-1.5 h-10 pl-1 pr-2.5 rounded-full bg-white/70 border border-white/80 hover:bg-white transition-colors">
                  {photoUrl ? <img src={photoUrl} alt="" className="h-8 w-8 rounded-full object-cover border-2 border-amber-300 shadow-sm" data-testid="sales-user-avatar-photo" /> : <span className="h-8 w-8 rounded-full bg-gradient-to-br from-teal-500 to-sky-500 text-white text-[11px] font-bold flex items-center justify-center shadow-sm">{initials(user?.full_name)}</span>}
                  <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <div className="px-2 py-2">
                  <p className="text-sm font-semibold text-slate-900">{user?.full_name}</p>
                  <p className="text-xs text-slate-500">@{user?.username}</p>
                  <span className="mt-1.5 inline-flex items-center rounded-full bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">{t.role[user?.role] || user?.role}</span>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwdOpen(true)} data-testid="sales-menu-change-password"><KeyRound className="h-4 w-4 mr-2" /> {t.changePassword}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSigOpen(true)} data-testid="sales-menu-signature"><PenLine className="h-4 w-4 mr-2" /> {t.mySignature}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPhotoOpen(true)} data-testid="sales-menu-photo"><Camera className="h-4 w-4 mr-2" /> {t.photo.menu}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); toast.success(t.logout); }} data-testid="sales-menu-logout" className="text-destructive focus:text-destructive"><LogOut className="h-4 w-4 mr-2" /> {t.logout}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 lg:px-10 py-5 sm:py-8 pb-28 lg:pb-10 max-w-[1400px] w-full mx-auto">
        <Outlet context={{ openNewLead: () => setNewLeadOpen(true) }} />
      </main>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 px-3 pb-[max(env(safe-area-inset-bottom),10px)] pt-2" data-testid="sales-mobile-nav">
        <div className="sales-glass-strong rounded-full px-2 py-1.5 grid grid-cols-4 gap-1">
          {nav.map((n) => (
            <NavLink key={n.id} to={n.to} end={n.end} data-testid={`sales-mobile-nav-${n.id}`} className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 h-12 rounded-full text-[10px] font-semibold transition-colors ${isActive ? 'bg-teal-600 text-white' : 'text-slate-600'}`}>
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
          <button type="button" onClick={() => setNewLeadOpen(true)} data-testid="sales-mobile-nav-new-lead" className="flex flex-col items-center justify-center gap-0.5 h-12 rounded-full text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 active:bg-teal-600 active:text-white transition-colors">
            <Plus className="h-5 w-5" /> {t.newLead}
          </button>
        </div>
      </nav>

      <LeadFormDialog open={newLeadOpen} onOpenChange={setNewLeadOpen} onSaved={(lead) => navigate(`/leads/${lead.id}?tab=simulation`)} />
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      <SignatureDialog open={sigOpen} onOpenChange={setSigOpen} />
      <RepPhotoDialog open={photoOpen || photoRequired} onOpenChange={setPhotoOpen} required={photoRequired} />
    </div>
    </SalesNotificationsProvider>
  );
}
